import { useQuery } from "@tanstack/react-query";
import type { AuthSession, AuthUser, HwAuthSessionResponse } from "@rondel/shared";

interface LogoutResponse {
  success: true;
  redirectTo?: string;
}

interface LogoutRequest {
  redirectTo?: string;
}

const sessionQueryKey = ["auth", "session"] as const;
export const DEV_AUTH_USER_ID_KEY = "rondel.devAuthUserId";

function getDevUserId() {
  return import.meta.env.VITE_DEV_USER?.trim() || "";
}

export function isDevAuthBypassEnabled() {
  return !!getDevUserId();
}

function getDevSession(): AuthSession | null {
  const userId = getDevUserId();
  if (!userId) return null;
  return {
    user: {
      id: userId,
      email: `${userId}@local.dev`,
      name: "Dev User",
    },
  };
}

function normalizeUser(user: AuthUser): AuthSession {
  return {
    user: {
      id: user.id,
      email: user.email,
      name: user.name,
      image: user.image,
      emailVerified: user.emailVerified,
      createdAt: user.createdAt,
    },
  };
}

export async function getSession(): Promise<AuthSession | null> {
  const devSession = getDevSession();
  if (devSession) {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(DEV_AUTH_USER_ID_KEY, devSession.user.id);
    }
    return devSession;
  }

  const res = await fetch("/auth/session", {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Session request failed (${res.status})`);
  }

  const payload = (await res.json()) as HwAuthSessionResponse;
  if (!payload.user) {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(DEV_AUTH_USER_ID_KEY);
    }
    return null;
  }

  const session = normalizeUser(payload.user);
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(DEV_AUTH_USER_ID_KEY, session.user.id);
  }
  return session;
}

export async function logout(request: LogoutRequest = {}): Promise<LogoutResponse> {
  if (isDevAuthBypassEnabled()) {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(DEV_AUTH_USER_ID_KEY);
    }
    return {
      success: true,
      redirectTo: request.redirectTo,
    };
  }

  const res = await fetch("/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    throw new Error(`Logout failed (${res.status})`);
  }

  if (typeof window !== "undefined") {
    window.sessionStorage.removeItem(DEV_AUTH_USER_ID_KEY);
  }
  return (await res.json()) as LogoutResponse;
}

export function beginGoogleLogin(returnTo: string) {
  if (isDevAuthBypassEnabled()) {
    window.location.assign(returnTo);
    return;
  }

  const params = new URLSearchParams({ returnTo });
  window.location.assign(`/auth/login/google?${params.toString()}`);
}

export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: getSession,
  });
}

export { sessionQueryKey };
